import React, { useCallback, useEffect, useRef, useState } from 'react';
import './LearningWorkspace.css';

export interface ResizableLearningSplitProps {
  materialVisible: boolean;
  activeNarrowSurface: 'material' | 'teaching';
  materialMinPx?: number;
  teachingMinPx?: number;
  initialMaterialPercent?: number;
  onNarrowModeChange?: (narrow: boolean) => void;
  onLayoutChange?: (layout: LearningSplitLayout) => void;
  onResizeEnd?: (materialPercent: number, direction: ResizeDirection) => void;
  material: React.ReactNode;
  teaching: React.ReactNode;
}

export type ResizeDirection = 'material_expanded' | 'teaching_expanded';
export interface LearningSplitLayout {
  materialPercent: number;
  teachingPercent: number;
  narrowMode: boolean;
  activeSurface: 'material' | 'teaching' | 'split';
}

const KEYBOARD_STEP_PX = 16;
const KEYBOARD_LARGE_STEP_PX = 48;

function clampMaterialPercent(
  requestedPx: number,
  containerWidth: number,
  materialMinPx: number,
  teachingMinPx: number,
) {
  const minMaterial = materialMinPx;
  const maxMaterial = Math.max(minMaterial, containerWidth - teachingMinPx);
  const nextMaterialPx = Math.min(maxMaterial, Math.max(minMaterial, requestedPx));
  return (nextMaterialPx / containerWidth) * 100;
}

function ariaPercent(percent: number) {
  return Math.round(percent);
}

export function ResizableLearningSplit({
  materialVisible,
  activeNarrowSurface,
  materialMinPx = 360,
  teachingMinPx = 560,
  initialMaterialPercent = 40,
  onNarrowModeChange,
  onLayoutChange,
  onResizeEnd,
  material,
  teaching,
}: ResizableLearningSplitProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const materialPercentRef = useRef(initialMaterialPercent);
  const activePointerIdRef = useRef<number | null>(null);
  const keyboardChangedRef = useRef(false);
  const interactionStartPercentRef = useRef<number | null>(null);
  const onNarrowModeChangeRef = useRef(onNarrowModeChange);
  const onLayoutChangeRef = useRef(onLayoutChange);
  const reportedNarrowModeRef = useRef<boolean | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [materialPercent, setMaterialPercent] = useState(initialMaterialPercent);
  const isNarrow = containerWidth !== null && containerWidth < materialMinPx + teachingMinPx;
  const separatorAvailable = materialVisible && !isNarrow;

  onNarrowModeChangeRef.current = onNarrowModeChange;
  onLayoutChangeRef.current = onLayoutChange;

  const updateMaterialPercent = useCallback((requestedPx: number) => {
    if (containerWidth === null || isNarrow) return false;

    const nextPercent = clampMaterialPercent(requestedPx, containerWidth, materialMinPx, teachingMinPx);
    if (nextPercent === materialPercentRef.current) return false;
    materialPercentRef.current = nextPercent;
    setMaterialPercent(nextPercent);
    return true;
  }, [containerWidth, isNarrow, materialMinPx, teachingMinPx]);

  useEffect(() => {
    if (!separatorAvailable) {
      activePointerIdRef.current = null;
      interactionStartPercentRef.current = null;
    }
  }, [separatorAvailable]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      const narrow = nextWidth < materialMinPx + teachingMinPx;
      setContainerWidth(nextWidth);
      if (reportedNarrowModeRef.current !== narrow) {
        reportedNarrowModeRef.current = narrow;
        onNarrowModeChangeRef.current?.(narrow);
      }

      if (!narrow && nextWidth > 0) {
        const nextPercent = clampMaterialPercent(
          (materialPercentRef.current / 100) * nextWidth,
          nextWidth,
          materialMinPx,
          teachingMinPx,
        );
        materialPercentRef.current = nextPercent;
        setMaterialPercent(nextPercent);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [materialMinPx, teachingMinPx]);

  useEffect(() => {
    const activeSurface = !materialVisible
      ? 'teaching'
      : isNarrow ? activeNarrowSurface : 'split';
    const appliedMaterialPercent = !materialVisible
      ? 0
      : isNarrow ? (activeNarrowSurface === 'material' ? 100 : 0) : materialPercent;
    onLayoutChangeRef.current?.({
      materialPercent: appliedMaterialPercent,
      teachingPercent: 100 - appliedMaterialPercent,
      narrowMode: isNarrow,
      activeSurface,
    });
  }, [activeNarrowSurface, isNarrow, materialPercent, materialVisible]);

  const finishResize = useCallback(() => {
    const startedAt = interactionStartPercentRef.current;
    interactionStartPercentRef.current = null;
    if (startedAt === null || Math.abs(materialPercentRef.current - startedAt) < 0.001) return;
    onResizeEnd?.(
      materialPercentRef.current,
      materialPercentRef.current > startedAt ? 'material_expanded' : 'teaching_expanded',
    );
  }, [onResizeEnd]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!separatorAvailable) return;
    interactionStartPercentRef.current = materialPercentRef.current;
    activePointerIdRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== activePointerIdRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    updateMaterialPercent(event.clientX - (rect?.left ?? 0));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== activePointerIdRef.current) return;
    activePointerIdRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    finishResize();
  };

  const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== activePointerIdRef.current) return;
    activePointerIdRef.current = null;
    interactionStartPercentRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleLostPointerCapture = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerId === activePointerIdRef.current) {
      activePointerIdRef.current = null;
      interactionStartPercentRef.current = null;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    if (!keyboardChangedRef.current) interactionStartPercentRef.current = materialPercentRef.current;
    const step = event.shiftKey ? KEYBOARD_LARGE_STEP_PX : KEYBOARD_STEP_PX;
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    keyboardChangedRef.current = updateMaterialPercent(
      (materialPercentRef.current / 100) * (containerWidth ?? 0) + direction * step,
    ) || keyboardChangedRef.current;
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') || !keyboardChangedRef.current) return;
    keyboardChangedRef.current = false;
    finishResize();
  };

  const minPercent = containerWidth !== null && containerWidth > 0 ? (materialMinPx / containerWidth) * 100 : 0;
  const maxPercent = containerWidth !== null && containerWidth > 0
    ? (Math.max(materialMinPx, containerWidth - teachingMinPx) / containerWidth) * 100
    : 100;
  const showMaterial = materialVisible && (!isNarrow || activeNarrowSurface === 'material');
  const showTeaching = !isNarrow || !materialVisible || activeNarrowSurface === 'teaching';

  return (
    <div
      ref={containerRef}
      className={`learning-workspace-split${isNarrow ? ' learning-workspace-split--narrow' : ''}`}
      data-testid="learning-workspace-split"
    >
      {showMaterial && (
        <section
          className="learning-workspace-split__material"
          data-testid="learning-material-surface"
          style={{
            overflow: 'hidden',
            ...(isNarrow
              ? { height: '100%', minHeight: 0 }
              : { flexBasis: `${materialPercent}%` }),
          }}
        >
          {material}
        </section>
      )}
      {separatorAvailable && (
        <div
          role="separator"
          tabIndex={0}
          aria-label="Resize learning material and teaching surfaces"
          aria-orientation="vertical"
          aria-valuemin={ariaPercent(minPercent)}
          aria-valuemax={ariaPercent(maxPercent)}
          aria-valuenow={ariaPercent(materialPercent)}
          className="learning-workspace-split__separator"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handleLostPointerCapture}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
        />
      )}
      {showTeaching && (
        <section className="learning-workspace-split__teaching" data-testid="learning-teaching-surface">
          {teaching}
        </section>
      )}
    </div>
  );
}
